import { useState } from 'react';
import { ChevronDown, History } from 'lucide-react';
import type { RunSummary } from '../../types/scheduledCommands';
import { SectionHeader } from '../ui/SectionHeader';
import { SkeletonLoader } from '../SkeletonLoader';
import { RelativeTime, RunStatusIcon, TRIGGER_LABEL, runOutcome } from './ScheduleBits';
import { cn } from '../../utils/cn';

interface RunHistoryProps {
  runs: RunSummary[] | null;
  error: string | null;
  onRetry: () => void;
  /** Offered when the list may be cut short. */
  onLoadMore?: () => void;
}

/** Who started a run, in words: the timer, or a named person, and for whom. */
function who(run: RunSummary): string {
  const actor = run.actor ? run.actor.split('@')[0] : null;
  if (run.trigger === 'player') return `Sent to ${run.player ?? 'a player'}${actor ? ` by ${actor}` : ''}`;
  if (run.trigger === 'manual') return `Run now${actor ? ` by ${actor}` : ''}`;
  return TRIGGER_LABEL.schedule;
}

/**
 * The last runs of one schedule, newest first. A run that missed servers
 * opens to name them, which is the question staff come here to answer.
 */
export const RunHistory = ({ runs, error, onRetry, onLoadMore }: RunHistoryProps) => {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <div className="glass-card p-4 md:p-5">
      <SectionHeader title="Run history" icon={History} count={runs?.length ?? null} description="Kept for 14 days." />
      {error && !runs ? (
        <div className="text-sm text-red-400 flex items-center justify-between gap-2">
          <span>{error}</span>
          <button type="button" onClick={onRetry} className="glass-button-secondary shrink-0">Retry</button>
        </div>
      ) : !runs ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => <SkeletonLoader key={i} className="h-9" />)}
        </div>
      ) : runs.length === 0 ? (
        <p className="text-sm text-glass-400">It has not run yet.</p>
      ) : (
        <>
          <ul className="divide-y divide-glass-200/10 -mx-1">
            {runs.map((run) => {
              const expandable = run.failed.length > 0 || !!run.error;
              const expanded = open === run.id;
              return (
                <li key={run.id}>
                  <button
                    type="button"
                    onClick={() => expandable && setOpen(expanded ? null : run.id)}
                    className={cn('w-full flex items-center gap-2.5 px-1 py-2 text-left rounded-md', expandable ? 'hover:bg-glass-200/5' : 'cursor-default')}
                    aria-expanded={expandable ? expanded : undefined}
                  >
                    <RunStatusIcon run={run} />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm text-glass-300 truncate">{who(run)}</span>
                      <RelativeTime at={run.started_at} className="block text-xs text-glass-500" />
                    </span>
                    <span className="font-mono text-xs text-glass-300 tabular-nums shrink-0">
                      {run.status === 'error' ? 'Error' : runOutcome(run)}
                    </span>
                    {expandable && (
                      <ChevronDown className={cn('w-4 h-4 shrink-0 text-glass-500 transition-transform', expanded && 'rotate-180')} />
                    )}
                  </button>
                  {expanded && (
                    <div className="px-1 pb-2 space-y-1">
                      {run.error && <p className="text-xs text-red-400 break-words">{run.error}</p>}
                      {run.failed.length > 0 && (
                        <>
                          <p className="text-xs text-glass-500">Did not run on</p>
                          <ul className="space-y-0.5">
                            {run.failed.map((server) => (
                              <li key={server} className="font-mono text-xs text-glass-300 break-all">{server}</li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          {onLoadMore && (
            <button type="button" onClick={onLoadMore} className="btn-ghost btn-xs mt-2">
              Show older runs
            </button>
          )}
        </>
      )}
    </div>
  );
};
